/**
 * The contact record. CRM-BUILD item 31, wave 5's first surface.
 *
 * ┌─ ⛔ WHAT THIS SURFACE IS FOR, AND WHAT IT IS EXPLICITLY NOT ────────────────┐
 * │ It is the PERSON's surface. The inbox panel is about a thread and shows the │
 * │ number that thread is with; this shows all of them, every booking, every    │
 * │ open task and every custom field the field engine says applies.             │
 * │                                                                            │
 * │ ⚠️ SPACING AND COLOUR ARE NOT FINISHED HERE AND THAT IS SCHEDULED, NOT      │
 * │ NEGLECTED. VISUAL-DESIGN is now an entry in the plan and runs after         │
 * │ CRM-BUILD closes. This is built against tokens so the pass has one system   │
 * │ to work on rather than a page of hex literals to unpick.                    │
 * └────────────────────────────────────────────────────────────────────────────┘
 *
 * ┌─ ⛔ THE RULE THIS PAGE GOT WRONG, AND THE CORRECTION ───────────────────────┐
 * │ The first version applied INBOX's "no empty cards" literally: a section with │
 * │ nothing in it was not rendered. Amir's inspection: **"an empty contact reads │
 * │ identically to a broken one."** 10001 showed "0 bookings, 0.00, not set,     │
 * │ empty, empty" and nothing distinguished a contact who has done nothing from  │
 * │ a page that failed to load.                                                 │
 * │                                                                            │
 * │ ⛔ THE RULE WAS WORKING AS WRITTEN AND WRONG AS APPLIED. It removes the card │
 * │ and leaves NO STATEMENT. **"No bookings yet" is content, not a frame around  │
 * │ absence** - the empty card it forbids is one that implies the feature exists │
 * │ and this customer has none, which is a different thing from saying so.      │
 * │                                                                            │
 * │ ⚠️ AND IT IS THE SAME CLASS AS THE DUPLICATE DETECTOR SHIPPING SILENTLY OFF, │
 * │ one layer up: absent, zero and not-applicable collapsed into one blank. The  │
 * │ configured-to-nothing class at the presentation layer.                     │
 * │ `a-surface-says-nothing-about-what-it-lacks`.                               │
 * └────────────────────────────────────────────────────────────────────────────┘
 */
import { useEffect, useState } from 'react'
import { navigate, setNavigationGuard } from '../routing.js'
import { NewBookingDialog } from '../bookings/NewBookingDialog.js'
import { ConfirmDialog } from '../ConfirmDialog.js'
import {
  fetchContactRecord,
  setContactField,
  type ContactRecordResponse,
  type RecordField,
} from '../data/contacts-api.js'
import { formatMoney } from '../data/money.js'
import './contact-record.css'

type Props = {
  reference: number
  onOpenThread: (conversationId: string) => void
}

export function ContactRecord({ reference, onOpenThread }: Props) {
  const [record, setRecord] = useState<ContactRecordResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [creatingBooking, setCreatingBooking] = useState(false)

  /**
   * ┌─ ⛔ EDITS ARE HELD, NOT WRITTEN. Amir 2026-09-12: edits need Save and ─────┐
   * │   Cancel, and leaving with unsaved changes prompts.                        │
   * │                                                                           │
   * │ Until today every field wrote on BLUR. Tabbing through a form saved each    │
   * │ box as you left it, so there was no moment at which somebody had made a     │
   * │ change and could still take it back - **Cancel had nothing to cancel** -    │
   * │ and a half-finished edit was already in the database.                      │
   * └───────────────────────────────────────────────────────────────────────────┘
   *
   * ⛔ THE PATCH ENDPOINT AND ITS FIELD CHECK DO NOT MOVE. It is still one field
   * per request, which is the shape a record page edits in and the reason a
   * whole-record PUT was rejected. **What changed is WHEN it fires.**
   *
   * ⚠️ KEYED BY FIELD ID AND HOLDING ONLY WHAT DIFFERS, so `dirty` is "is this
   * object empty" rather than a flag somebody has to remember to clear.
   */
  const [drafts, setDrafts] = useState<Record<string, string>>({})
  const [problems, setProblems] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState(false)
  const dirty = Object.keys(drafts).length > 0

  /**
   * ⛔ THE NAVIGATION THE GUARD REFUSED, HELD UNTIL SOMEBODY ANSWERS. `null`
   * means nothing is pending and the dialog is not rendered.
   *
   * ⚠️ `useState(() => retry)` RATHER THAN `useState(retry)`: a function passed
   * to a setter is treated as an updater and CALLED, so storing one directly
   * would navigate immediately - the dialog would flash and the work would be
   * gone, which is the opposite of what it is for.
   */
  const [leaving, setLeaving] = useState<(() => void) | null>(null)

  useEffect(() => {
    let current = true
    setRecord(null)
    setError(null)
    /** ⚠️ A different contact starts with no drafts. Carrying them across would
     *  write one contact's values onto another's fields. */
    setDrafts({})
    setProblems({})
    void fetchContactRecord(reference)
      .then((r) => {
        if (current) setRecord(r)
      })
      .catch(() => {
        if (current) setError('That contact could not be opened.')
      })
    return () => {
      current = false
    }
  }, [reference])

  /**
   * ⛔ THE UNSAVED-CHANGES PROMPT, IN TWO HALVES BECAUSE LEAVING HAS TWO SHAPES.
   *
   *   `beforeunload`  closing the tab, reloading, following a link out
   *   the nav guard    every in-app navigation, Back included
   *
   * ⚠️ NEITHER IS OPTIONAL. Without the first, a reload loses the edit with no
   * prompt; without the second, clicking Contacts in the nav does - and the
   * second is the one people actually do.
   *
   * ⚠️ AND THE GUARD IS RELEASED ON UNMOUNT, or a record that has been closed
   * goes on refusing navigations for the life of the session.
   */
  useEffect(() => {
    if (!dirty) return undefined
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      /** ⚠️ The browser shows its own wording; assigning a string is what makes
       *  older engines prompt at all. */
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', warn)
    /**
     * ⛔ THE GUARD REFUSES AND REMEMBERS, rather than asking. Amir's finding 7:
     * the prompt must be the platform's modal. `window.confirm` is synchronous
     * and a modal is not, so the guard returns `false` and keeps the `retry` -
     * the dialog below calls it when somebody confirms.
     */
    setNavigationGuard((retry) => {
      setLeaving(() => retry)
      return false
    })
    return () => {
      window.removeEventListener('beforeunload', warn)
      setNavigationGuard(null)
    }
  }, [dirty])

  const reload = () => {
    void fetchContactRecord(reference).then(setRecord).catch(() => undefined)
  }

  /**
   * ⛔ ONE REQUEST PER CHANGED FIELD, IN SEQUENCE, AND A FAILURE STOPS NOTHING
   * ELSE. Three edits where the middle one is refused must leave the other two
   * saved and the refused one still in the box with its reason - not roll the
   * batch back, which would discard work the server accepted.
   *
   * ⚠️ SO A PARTIAL SAVE IS A REAL STATE AND IT IS VISIBLE: the fields that
   * saved lose their draft, the one that did not keeps it and shows what the
   * server said.
   */
  const save = () => {
    if (record === null) return
    setSaving(true)
    const entries = Object.entries(drafts)
    void (async () => {
      const stillWrong: Record<string, string> = {}
      const stillDraft: Record<string, string> = {}
      for (const [fieldId, value] of entries) {
        try {
          await setContactField(reference, fieldId, value)
        } catch (e) {
          stillWrong[fieldId] = (e as Error).message
          stillDraft[fieldId] = value
        }
      }
      setProblems(stillWrong)
      setDrafts(stillDraft)
      setSaving(false)
      reload()
    })()
  }

  /** ⛔ Cancel puts every box back to what the record says, which is what makes
   *  the draft worth holding in the first place. */
  const cancel = () => {
    setDrafts({})
    setProblems({})
  }

  if (error !== null) {
    return (
      <main className="record" data-testid="contact-record-error">
        <p className="record__empty">{error}</p>
      </main>
    )
  }
  /**
   * ⚠️ A SKELETON RATHER THAN A SPINNER, per §13's cross-cutting pattern:
   * *"structure renders immediately, content fills in. Never a full-page
   * spinner."* ⛔ The skeleton obligation is one of the four VISUAL-DESIGN now
   * owns; this is the shape, not the finished treatment.
   */
  if (record === null) {
    return (
      <main className="record" data-testid="contact-record-loading">
        <div className="record__skeleton record__skeleton--title" />
        <div className="record__skeleton" />
        <div className="record__skeleton" />
      </main>
    )
  }

  const name =
    [record.firstName, record.lastName].filter(Boolean).join(' ') ||
    record.displayName ||
    `Contact ${record.reference}`

  return (
    <main className="record" data-testid="contact-record" data-contact-reference={record.reference}>
      <header className="record__header">
        <div>
          <h1 className="record__name">{name}</h1>
          {/* ⚠️ The reference, not the uuid. It is what staff say aloud. */}
          <p className="record__reference">#{record.reference}</p>
        </div>
        {/**
         * ⛔ **New Booking**, WHERE THE WIREFRAME DRAWS IT. Built 2026-09-15.
         * `createBooking` had existed since wave 3 with no screen calling it, so
         * a CRM's central entity could not be created by a person - the board
         * showed only what the seeder had made.
         *
         * ⚠️ AND IT OPENS WHAT IT MADE. A create that leaves you on the page you
         * started from makes you go and find the thing, which is the shape the
         * board's card click already avoids.
         */}
        <button
          type="button"
          className="record__new-booking"
          data-testid="contact-new-booking"
          onClick={() => setCreatingBooking(true)}
        >
          New Booking
        </button>
        {record.blockedAt !== null && (
          <span className="record__blocked" data-testid="contact-blocked">
            Blocked
          </span>
        )}
      </header>

      <section className="record__stats">
        <Stat label="Bookings" value={String(record.bookingCount)} />
        {/**
         * ⛔ THIS CELL CARRIED A COMMENT THAT WAS WRONG, AND THE COMMENT IS THE
         * FINDING. It read: *"No currency symbol. §4.26: the account currency is
         * a display setting and this page does not have it yet, so it shows the
         * number rather than guessing a symbol. Confirmed correct at
         * inspection."* Every clause of that was mine, and
         * `org_settings.account_currency` had existed since migration 0023.
         *
         * ⚠️ SO "CONFIRMED CORRECT AT INSPECTION" CONFIRMED NOTHING. It recorded
         * that a defect had been looked at and reasoned about, which is what
         * made it survive the inspection it names - the same shape as the
         * premise that was never true rather than expired. The label was
         * hardcoded for the same reason: `value_label` was there too.
         */}
        <Stat label={record.valueLabel} value={formatMoney(record.valueTotal, record.currency, record.decimalPlaces)} />
        {/* ⛔ "Created", NOT "Customer since". Amir's ruling 2026-09-11: the
            label states what the value IS. A contact becomes a customer when
            they book, and this is when the row appeared - which for most of
            this corpus is when a message arrived. For a contact with zero
            bookings, "Customer since" asserts something untrue. */}
        <Stat label="Created" value={record.createdAtDisplay ?? "unknown"} />
        {/* ⛔ WHO OWNS THIS CONTACT, and "Unassigned" is stated rather than
            blank: nobody owning it is a fact somebody may need to act on. */}
        {/**
         * ⛔ "RESPONSIBLE USER", NOT "OWNER". Amir 2026-09-12: it is his term and
         * Kommo's, which is what his staff have already learned.
         *
         * ⚠️ IT IS A LABEL, NOT A COLUMN. `assigned_user_id` does not move, and
         * neither does `assignedUser` in the payload - this is exactly the
         * `value_label` shape, a word the interface says.
         *
         * ⚠️ AND IT IS NOT IN `org_settings`, WHICH IS THE DIFFERENCE FROM
         * `value_label`: Amir renamed it once for everybody rather than making it
         * configurable, so a setting would be a control nobody asked for with a
         * default nobody would change. The contacts list's `assigned` column
         * carries the same words from `list-columns.ts`.
         */}
        <Stat label="Responsible User" value={record.assignedUser?.name ?? 'Unassigned'} />
      </section>

      {/**
       * ⛔ CONTACT NOTES LEAD THE RECORD. Amir's ruling 2026-09-12: a contact
       * note is true of the person regardless of any booking - *"Nervous diver,
       * needs extra briefing time"* - so it sits above everything that is about
       * one reservation.
       *
       * ⚠️ AND THIS ONE DOES CARRY AN EMPTY STATE, unlike the booking cards.
       * The difference is that it is asserting something about the PERSON, and
       * "nobody has written anything about this diver" is a fact worth stating
       * once at the top of their record.
       */}
      <Section
        title="About this contact"
        testid="record-notes"
        empty={record.notes.length === 0}
        emptyText="No notes about this contact."
      >
        <ul className="record__list">
          {record.notes.map((n) => (
            <li
              key={n.id}
              className={n.pinnedAt === null ? 'record__note' : 'record__note record__note--pinned'}
              data-testid="record-note"
            >
              {n.body}{' '}
              <span className="record__muted">
                {n.actorType === 'agent'
                  ? [n.authorFirstName, n.authorLastName].filter(Boolean).join(' ') || 'an agent'
                  : 'Automated'}
                {' · '}
                {n.createdAtDisplay}
                {n.editedAt !== null && ' · edited'}
                {n.pinnedAt !== null && ' · pinned'}
              </span>
            </li>
          ))}
        </ul>
      </Section>

      <Section title="Reachable at" testid="record-identities">
        <ul className="record__list">
          {record.identities.map((i) => (
            <li key={i.id} className="record__identity">
              <span className="record__identity-kind">{i.kind}</span>
              <span>{i.identifier ?? 'no identifier'}</span>
              {i.blockedAt !== null && <span className="record__blocked-pill">blocked</span>}
              {/**
               * ⛔ AN ENDPOINT WITH NO THREAD SAYS SO. Amir's inspection of
               * 10000: a WhatsApp number listed here, two Instagram threads
               * below, and nothing explaining the WhatsApp number. It has never
               * been messaged - the page was right and read as though a thread
               * were missing.
               */}
              {i.threadCount === 0 && (
                <span className="record__muted" data-testid="identity-no-thread">
                  no conversation
                </span>
              )}
            </li>
          ))}
        </ul>
      </Section>

      {/**
       * ⛔ THE THREADS, WHICH IS THE DISCLOSURE `contact-with-several-threads-
       * shows-only-one` ASKED FOR. A contact with three conversations now has a
       * surface that says so, and the order is the inbox's own.
       */}
      {/**
       * ⛔ A THREAD ROW IS NOW DISTINGUISHABLE, WHICH IT WAS NOT. Every row was
       * labelled with the CUSTOMER's identity - the same for all of them by
       * construction - while our number, the thing that differs, was absent.
       *
       * ⛔ AND THE DATE IS LOAD-BEARING RATHER THAN DECORATIVE: under the
       * ordering ruling the first row is the one `/inbox/contacts/<n>` opens,
       * and nothing on screen said why it was first.
       */}
      <Section title="Conversations" testid="record-threads" empty={record.threads.length === 0}
        emptyText="No conversations yet.">
        <ul className="record__list">
          {record.threads.map((t) => (
            <li key={t.id}>
              <button
                type="button"
                className="record__thread"
                data-conversation-id={t.id}
                data-our-number={t.ourNumber}
                onClick={() => onOpenThread(t.id)}
              >
                <span className="record__thread-head">
                  <span className="record__identity-kind">{t.channel}</span>
                  {/* ⚠️ OUR number, labelled as ours. "via" is doing real work:
                      without it a reader cannot tell whose number this is. */}
                  <span className="record__muted">via {t.ourNumber}</span>
                  <span className="record__muted">{t.status}</span>
                  <span className="record__thread-date">
                    {t.lastMessageAtDisplay ?? 'no messages'}
                  </span>
                </span>
                <span className="record__preview">
                  {t.preview === null
                    ? 'No messages in this conversation.'
                    : `${t.previewDirection === 'outbound' ? 'You: ' : ''}${t.preview}`}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </Section>

      <Section
        title={`${record.entityLabel}s`}
        testid="record-bookings"
        empty={record.bookings.length === 0}
        emptyText={`No ${record.entityLabel.toLowerCase()}s yet.`}
      >
        <ul className="record__list">
          {record.bookings.map((b) => (
            <li key={b.id} className="record__booking">
              {/**
               * ⛔ THE TITLE, WITH THE ENTITY LABEL AS ITS FALLBACK. Amir's
               * ruling: nullable, editable, settable by automation, NEVER
               * derived - so the fallback is rendered here rather than stored.
               * "Ride #10750" for a taxi firm, and item 39's build-time check
               * covers the interpolation.
               */}
              <span className="record__booking-title">
                {b.title ?? `${record.entityLabel} #${b.reference}`}
              </span>
              <span className="record__muted">{b.pipelineLabel}</span>
              {/* ⛔ The stage LABEL is rendered and nothing branches on it.
                  §4.18: no component may branch on a stage being called
                  "Confirmed", because a deployment may not have one. */}
              <span className={`record__stage record__stage--${b.stageKind}`}>{b.stageLabel}</span>
              {/**
               * ⛔ THE DATE SET. §4.17 made it configuration, and it was absent
               * from this page entirely - a booking with no date is a record
               * nobody can act on. The labels come from the fields, so a Courses
               * booking shows two and a Dive Trips booking shows one.
               */}
              {b.dates.length > 0 && (
                <span className="record__dates">
                  {b.dates.map((d) => `${d.label}: ${d.value}`).join(' · ')}
                </span>
              )}
              {/**
               * ⛔ NULL AND ZERO ARE DIFFERENT AND THE PAGE SAYS WHICH. A
               * booking with no value agreed yet is ordinary; one worth nothing
               * is not. ⚠️ The COLUMN cannot distinguish them either - both are
               * representable, and only the null carries the meaning "not
               * agreed". Rendering `value ?? ''` made them one blank.
               */}
              <span className="record__muted">
                {b.value === null ? 'no value yet' : formatMoney(b.value, record.currency, record.decimalPlaces)}
              </span>
              {/**
               * ⛔ THIS BOOKING'S OWN NOTES, BENEATH ITS OWN CARD. Amir's ruling
               * 2026-09-12. **Contact notes are deliberately NOT repeated here**
               * - they are about the person, and a note shown under every
               * booking stops being about anything in particular.
               *
               * ⚠️ AND NO EMPTY STATE, WHICH IS THE OPPOSITE OF THE RULE
               * ELSEWHERE ON THIS PAGE AND IS DELIBERATE. "No notes" under five
               * booking cards is five statements nobody needs; the card is
               * already a summary, and item 11's argument applies - an empty
               * card is a claim about the customer where a named gap is a
               * statement about the product.
               */}
              {b.notes.length > 0 && (
                <ul className="record__booking-notes" data-testid="record-booking-notes">
                  {b.notes.map((n) => (
                    <li key={n.id} className="record__booking-note">
                      {n.body}{' '}
                      <span className="record__muted">
                        {n.actorType === 'agent'
                          ? [n.authorFirstName, n.authorLastName].filter(Boolean).join(' ') ||
                            'an agent'
                          : 'Automated'}
                        {' · '}
                        {n.createdAtDisplay}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
        {record.bookingTotal > record.bookings.length && (
          <p className="record__more" data-testid="record-bookings-more">
            Showing {record.bookings.length} of {record.bookingTotal}. Open stages first.
          </p>
        )}
      </Section>

      {/**
       * ⚠️ "No open tasks" RATHER THAN NOTHING, and that distinction is finding
       * 8 again: 10050 had a task and it was COMPLETED, so the section was
       * absent and looked identical to a contact that never had one.
       */}
      <Section
        title="Open tasks"
        testid="record-tasks"
        empty={record.tasks.length === 0}
        emptyText="No open tasks."
      >
        <ul className="record__list">
          {record.tasks.map((t) => (
            <li key={t.id} className="record__task">
              <span>{t.title}</span>
              <span className="record__muted">{t.dueAtDisplay}</span>
            </li>
          ))}
        </ul>
      </Section>

      <Section title="Tags" testid="record-tags" empty={record.tags.length === 0} emptyText="No tags.">
        <ul className="record__tags">
          {record.tags.map((t) => (
            <li key={t.id} className="record__tag">
              {t.label}
            </li>
          ))}
        </ul>
      </Section>

      <Section
        title="Details"
        testid="record-fields"
        empty={record.fields.length === 0}
        /* ⚠️ This one names the CAUSE rather than the state. No fields on a
           record means nobody has configured any, which is an admin action -
           unlike "no tags", which is just a fact about this contact. */
        emptyText="No custom fields are configured for contacts."
      >
        <dl className="record__fields">
          {record.fields.map((f) => (
            <FieldRow
              key={f.id}
              field={f}
              currency={record.currency}
              draft={drafts[f.id]}
              problem={problems[f.id] ?? null}
              onChange={(value) => {
                const shown = f.optionLabel ?? f.value ?? ''
                setDrafts((d) => {
                  const next = { ...d }
                  /** ⛔ TYPING A VALUE BACK TO WHAT IT WAS IS NOT A CHANGE. Without
                   *  this the bar stays up, Save writes a value nobody altered,
                   *  and leaving prompts about an edit that does not exist. */
                  if (value === shown) delete next[f.id]
                  else next[f.id] = value
                  return next
                })
              }}
            />
          ))}
        </dl>
      </Section>

      {/**
       * ⛔ SAVE AND CANCEL, AND THE BAR IS ONLY THERE WHEN THERE IS SOMETHING TO
       * SAVE. A permanently visible Save on a record nobody has touched is a
       * control that does nothing, which is the defect the row menu was cut down
       * for. **The bar appearing is also how somebody knows they have changed
       * something** - it is the unsaved state made visible, not just a control.
       */}
      {dirty && (
        <div className="record__editbar" role="status" data-testid="record-editbar">
          <span className="record__editbar-note">
            {Object.keys(drafts).length === 1
              ? '1 unsaved change'
              : `${Object.keys(drafts).length} unsaved changes`}
          </span>
          <button
            type="button"
            className="record__cancel"
            onClick={cancel}
            disabled={saving}
            data-testid="record-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            className="record__save"
            onClick={save}
            disabled={saving}
            data-testid="record-save"
          >
            {saving ? 'Saving' : 'Save'}
          </button>
        </div>
      )}

      {/**
       * ⛔ THE PLATFORM'S OWN CONFIRMATION, replacing `window.confirm`. Amir's
       * finding 7.
       *
       * ⚠️ AND IT COVERS IN-APP NAVIGATION ONLY. `beforeunload` above still
       * fires for a tab close, a reload, or a link off the site, and the browser
       * draws its own dialog there with its own words - it cannot be styled or
       * replaced, deliberately, because a page that could style it could imitate
       * the browser. **So two different prompts for two different actions, and
       * that is the platform rather than a decision.**
       */}
      {leaving !== null && (
        <ConfirmDialog
          title="Unsaved changes"
          body={
            Object.keys(drafts).length === 1
              ? 'One change on this contact has not been saved. Leaving will discard it.'
              : `${Object.keys(drafts).length} changes on this contact have not been saved. Leaving will discard them.`
          }
          confirmLabel="Discard and Leave"
          cancelLabel="Stay"
          onCancel={() => setLeaving(null)}
          onConfirm={() => {
            const go = leaving
            /** ⚠️ THE DRAFT IS CLEARED BEFORE NAVIGATING, which releases the
             *  guard. Navigating first would re-enter it on the way out. */
            setDrafts({})
            setProblems({})
            setLeaving(null)
            go()
          }}
        />
      )}

      {/** ⛔ IT OPENS THE THREAD IT MADE, which is the locked decision applied
       *  to a create: there is no booking page, so the destination is
       *  `/inbox/bookings/<n>` exactly as a card's click is. */}
      {creatingBooking && (
        <NewBookingDialog
          contactReference={reference}
          onClose={() => setCreatingBooking(false)}
          onCreated={(created) => {
            setCreatingBooking(false)
            navigate({ name: 'inbox', bookingReference: created })
          }}
        />
      )}
    </main>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="record__stat">
      <dt className="record__stat-label">{label}</dt>
      <dd className="record__stat-value">{value}</dd>
    </div>
  )
}

/**
 * ⛔ A SECTION WITH NOTHING IN IT SAYS SO, AND THAT IS THE CORRECTION AMIR'S
 * INSPECTION FORCED.
 *
 * The first version omitted the section entirely, on INBOX's "no empty cards"
 * rule. **The rule forbids a card that IMPLIES the feature exists and this
 * customer has none; it does not require silence.** Omitting the section made an
 * empty record and a broken one the same screen.
 *
 * ⚠️ SO `emptyText` IS REQUIRED WHEN `empty` IS PASSED, and each one says
 * something different: "No tags" is a fact about this contact, "No custom fields
 * are configured" is an admin action nobody has taken. Absent, zero and
 * not-applicable are three states and they get three sentences.
 */
function Section({
  title,
  testid,
  empty = false,
  emptyText,
  children,
}: {
  title: string
  testid: string
  empty?: boolean
  emptyText?: string
  children: React.ReactNode
}) {
  return (
    <section className="record__section" data-testid={testid} data-empty={empty ? 'true' : undefined}>
      <h2 className="record__section-title">{title}</h2>
      {empty ? (
        <p className="record__empty-line" data-testid={`${testid}-empty`}>
          {emptyText ?? 'Nothing yet.'}
        </p>
      ) : (
        children
      )}
    </section>
  )
}

/**
 * ⛔ ONE FIELD, EDITED IN PLACE, AND A RETIRED ONE IS MARKED RATHER THAN HIDDEN.
 * Item 19: *"a retired field shown unmarked reads as live."* `visibleFieldsFor`
 * decided it should be here; this says why it is.
 */
function FieldRow({
  field,
  currency,
  draft,
  problem,
  onChange,
}: {
  field: RecordField
  currency: string
  /**
   * ⛔ `undefined` MEANS "NOT EDITED", WHICH IS NOT THE SAME AS AN EMPTY STRING.
   * Clearing a field is a real edit and must survive as one, so the draft map
   * holds `''` for it rather than dropping the key.
   */
  draft: string | undefined
  problem: string | null
  onChange: (value: string) => void
}) {
  const shown = field.optionLabel ?? field.value ?? ''
  /**
   * ⛔ THE DRAFT LIVES ON THE RECORD NOW, NOT HERE, and that is what Save and
   * Cancel need. A draft per row could not be cancelled from one control, and
   * the row's own `useEffect` that followed a reload *"only when not editing"*
   * was a second rule about when a value is authoritative - which the one draft
   * map replaces with none.
   */
  const value = draft ?? shown

  /** ⚠️ A select stores an option id, so a text box would ask somebody to type a
   * uuid. Until the picker exists it is read-only and says so. */
  const editable = field.fieldType !== 'select' && field.fieldType !== 'radio'

  return (
    <div
      className={draft === undefined ? 'record__field' : 'record__field record__field--dirty'}
      data-testid="record-field"
      data-field-id={field.id}
      data-field-type={field.fieldType}
      data-dirty={draft === undefined ? undefined : 'true'}
    >
      <dt className="record__field-label">
        {field.label}
        {field.retired && (
          <span className="record__retired" data-testid="record-field-retired">
            retired
          </span>
        )}
      </dt>
      <dd className="record__field-value">
        {/**
         * ⛔ A `currency` FIELD SHOWS ITS UNIT BESIDE THE BOX, NOT INSIDE IT.
         * The box holds what gets written, so formatting the draft would mean
         * parsing a formatted string back out on every keystroke - and the
         * separator that formatting adds is exactly what the parser then has to
         * guess at. §4.26 gives the account one currency, so the unit is
         * constant and belongs next to the input rather than in it.
         *
         * ⚠️ AND THE ROW IS ITS OWN ELEMENT BECAUSE `record__field-value` IS A
         * COLUMN. The first version put the unit straight in, and the capture
         * showed "EUR" on the line UNDERNEATH the box - "beside it" was in the
         * comment and not in the layout, which is the shape the `aria-sort`
         * finding had a day earlier.
         */}
        <div className="record__field-input">
          {editable ? (
            <input
              className="record__input"
              aria-label={field.label}
              value={value}
              onChange={(e) => onChange(e.target.value)}
            />
          ) : (
            <span className="record__muted">{shown || 'not set'}</span>
          )}
          {field.fieldType === 'currency' && (
            <span className="record__unit" data-testid="record-field-unit">
              {currency}
            </span>
          )}
        </div>
        {problem !== null && (
          <span className="record__problem" role="status">
            {problem}
          </span>
        )}
      </dd>
    </div>
  )
}
